import type {
  DashboardSnapshot,
  InfrastructureSnapshot,
  PublicShowcaseSnapshot,
} from '../shared/types'
import type { TrafficDetail, TrafficOverview } from '../shared/traffic'

export interface ProblemDetail {
  type: string
  title: string
  status: number
  detail: string
  requestId?: string
}

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetail) {
    super(problem.detail)
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
      ...options?.headers,
    },
  })
  const body = (await response.json()) as T | ProblemDetail
  if (!response.ok) throw new ApiError(body as ProblemDetail)
  return body as T
}

export const api = {
  showcase: () =>
    request<PublicShowcaseSnapshot>('/api/v1/public/showcase', {
      cache: 'no-cache',
    }),
  session: () => request<{ authenticated: boolean }>('/api/v1/auth/session'),
  login: (password: string) =>
    request<{ authenticated: boolean }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  logout: () =>
    request<{ authenticated: boolean }>('/api/v1/auth/logout', { method: 'POST' }),
  status: () => request<DashboardSnapshot>('/api/v1/status'),
  infrastructure: (range: InfrastructureSnapshot['range']) =>
    request<InfrastructureSnapshot>(
      `/api/v1/infrastructure?range=${encodeURIComponent(range)}`,
    ),
  traffic: () => request<TrafficOverview>('/api/v1/traffic'),
  approveSleep: (project: string, approved: boolean) =>
    request<TrafficOverview>('/api/v1/traffic/approvals', {
      method: 'POST',
      body: JSON.stringify({ project, approved }),
    }),
  trafficDetail: (applicationId: string) =>
    request<TrafficDetail>(`/api/v1/traffic/${encodeURIComponent(applicationId)}`),
  run: () =>
    request<DashboardSnapshot>('/api/v1/checks/run', {
      method: 'POST',
    }),
  testNotification: () =>
    request<{ delivered: boolean; deliveredAt: string }>('/api/v1/notifications/test', {
      method: 'POST',
    }),
}
